import { useState } from "react";
import API from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";

export default function IntakeQReconnectDialog({ open, onOpenChange, onSuccess }) {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleReconnect = async (e) => {
    e.preventDefault();
    if (!email.trim() && !fullName.trim()) return;
    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      const { data } = await API.post("/intakeq/reconnect", {
        email: email.trim(),
        fullName: fullName.trim(),
      });

      if (data.status === "success") {
        setSuccess(true);
        setTimeout(() => {
          onSuccess?.();
          onOpenChange(false);
          setSuccess(false);
          setEmail("");
          setFullName("");
        }, 1500);
      } else {
        setError(data.message || "Could not reconnect. Please try again.");
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Reconnection failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="reconnect-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Reconnect IntakeQ Account</DialogTitle>
          <DialogDescription>
            Enter the email or name you used when registering with Elevated Psychiatry to reconnect your records.
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="flex flex-col items-center gap-3 py-6" data-testid="reconnect-success">
            <CheckCircle2 className="w-10 h-10 text-green-500" />
            <p className="text-sm font-medium text-foreground">Records connected successfully!</p>
          </div>
        ) : (
          <form onSubmit={handleReconnect} className="space-y-4">
            <div>
              <Label className="text-sm font-medium">IntakeQ Email</Label>
              <Input
                data-testid="reconnect-email"
                type="email"
                placeholder="your-intakeq@email.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="mt-1.5 h-12"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Full Name (optional)</Label>
              <Input
                data-testid="reconnect-name"
                type="text"
                placeholder="First Last"
                value={fullName}
                onChange={e => setFullName(e.target.value)}
                className="mt-1.5 h-12"
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex gap-2" data-testid="reconnect-error">
                <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <Button
              data-testid="reconnect-submit-button"
              type="submit"
              className="w-full h-12 rounded-full"
              disabled={loading || (!email.trim() && !fullName.trim())}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reconnect Account"}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
